import { createSlice, createEntityAdapter, Update } from '@reduxjs/toolkit'
import { StoreData } from '../reducers/typings'

import {
  ISalesFunnelClosedReasons,
  ISalesFunnel,
  ISalesFunnelStage,
} from './typings'
import {
  getSalesFunnelList,
  createSalesFunnel,
  deleteSalesFunnel,
  setDefaultSalesFunnel,
  getAllFunnelStages,
  getEntityClosedReasons,
  updateSalesFunnel,
} from './api'

export const salesFunnelAdapter = createEntityAdapter<ISalesFunnel>({
  selectId: (funnel) => funnel.id,
  sortComparer: (a, b) => a.name.localeCompare(b.name),
})

const salesFunnelStageAdapter = createEntityAdapter<ISalesFunnelStage>({
  sortComparer: (a, b) =>
    a.salesFunnelId === b.salesFunnelId
      ? a.order - b.order
      : a.salesFunnelName.localeCompare(b.salesFunnelName),
})

const salesFunnelClosedLostReasonsAdapter =
  createEntityAdapter<ISalesFunnelClosedReasons>({
    sortComparer: (a, b) => a.id - b.id,
  })

export const initialState = {
  closedLostReasons: salesFunnelClosedLostReasonsAdapter.getInitialState(),
  funnels: salesFunnelAdapter.getInitialState(),
  stages: salesFunnelStageAdapter.getInitialState(),
}

export const salesFunnel = createSlice({
  name: 'salesFunnelState',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getSalesFunnelList.fulfilled, (state, { payload }) => {
      salesFunnelAdapter.setAll(state.funnels, payload)
    })
    builder.addCase(createSalesFunnel.fulfilled, (state, { payload }) => {
      salesFunnelAdapter.addOne(state.funnels, payload)
    })
    builder.addCase(updateSalesFunnel.fulfilled, (state, { payload }) => {
      const { id, name } = payload
      salesFunnelAdapter.updateOne(state.funnels, { id, changes: { name } })
    })
    builder.addCase(deleteSalesFunnel.fulfilled, (state, { payload }) => {
      salesFunnelAdapter.removeOne(state.funnels, payload)
    })
    builder.addCase(setDefaultSalesFunnel.fulfilled, (state, { payload }) => {
      const updates: Update<ISalesFunnel>[] = []

      const current = localSelectors
        .selectAll(state.funnels)
        .filter((x) => x.isDefault)
        .pop()
      if (current) {
        updates.push({ id: current.id, changes: { isDefault: false } })
      }
      updates.push({ id: payload, changes: { isDefault: true } })

      salesFunnelAdapter.updateMany(state.funnels, updates)
    })
    builder.addCase(getAllFunnelStages.fulfilled, (state, { payload }) => {
      salesFunnelStageAdapter.setAll(state.stages, payload)
    })
    builder.addCase(getEntityClosedReasons.fulfilled, (state, { payload }) => {
      salesFunnelClosedLostReasonsAdapter.upsertOne(
        state.closedLostReasons,
        payload,
      )
    })
  },
})

export default salesFunnel.reducer

const localSelectors = salesFunnelAdapter.getSelectors()
export const salesFunnelAdapterSelectors =
  salesFunnelAdapter.getSelectors<StoreData>(
    (state) => state.salesFunnelState.funnels,
  )

export const {
  selectById: selectSalesFunnelById,
  selectIds: selectSalesFunnelIds,
  selectEntities: selectSalesFunnelEntities,
  selectAll: selectAllSalesFunnels,
  selectTotal: selectTotalSalesFunnels,
} = salesFunnelAdapterSelectors

export const salesFunnelStageAdapterSelectors =
  salesFunnelStageAdapter.getSelectors<StoreData>(
    (state) => state.salesFunnelState.stages,
  )

export const {
  selectAll: selectAllSalesFunnelStages,
  selectById: selectSalesFunnelStageById,
} = salesFunnelStageAdapterSelectors

export const salesFunnelClosedLostReasonsAdapterSelectors =
  salesFunnelClosedLostReasonsAdapter.getSelectors<StoreData>(
    (state) => state.salesFunnelState.closedLostReasons,
  )

export const { selectById: selectClosedLostReasons } =
  salesFunnelClosedLostReasonsAdapterSelectors
